// src/pages/billing/JournalEntryCreatePage.tsx

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useBilling, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';

export const JournalEntryCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useBilling();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    memo: '',
    type: 'JOURNAL_ENTRY_TYPE_STANDARD',
    entry_date: new Date().toISOString().split('T')[0],
  });
  const [lines, setLines] = useState([
    { account_id: '', debit: '', credit: '', description: '' },
    { account_id: '', debit: '', credit: '', description: '' },
  ]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (index: number, field: string, value: string) => {
    setLines((prev) => prev.map((line, i) => i === index ? { ...line, [field]: value } : line));
  };

  const addLine = () => {
    setLines((prev) => [...prev, { account_id: '', debit: '', credit: '', description: '' }]);
  };

  const removeLine = (index: number) => {
    if (lines.length <= 2) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const totalDebits = lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredits = lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isBalanced) return;
    setIsSubmitting(true);
    try {
      await store.CreateJournalEntry({
        description: formData.description,
        memo: formData.memo,
        type: formData.type,
        entry_date: formData.entry_date ? new Date(formData.entry_date).toISOString() : undefined,
        lines: {
          lines: lines.filter((l) => l.account_id).map((l) => ({
            account: { id: l.account_id },
            debit: l.debit ? { amount: Math.round(parseFloat(l.debit) * 100), currency: 'CURRENCY_USD', decimal_places: 2 } : undefined,
            credit: l.credit ? { amount: Math.round(parseFloat(l.credit) * 100), currency: 'CURRENCY_USD', decimal_places: 2 } : undefined,
            description: l.description,
          })),
        },
      } as any);
      navigate('/billing/journal-entries');
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div>
        <h1 className="sr-only">Create Journal Entry</h1>
        <nav className="text-sm">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/billing/journal-entries" className="text-muted-foreground hover:text-foreground">Journal Entries</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.create')}</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Entry Header</CardTitle>
              <CardDescription>Double-entry journal entry. Debits must equal credits.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input label="Description" type="text" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Entry description" required />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Memo</label>
                <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[60px]" value={formData.memo} onChange={(e) => handleChange('memo', e.target.value)} placeholder="Optional memo" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Type</label>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)} className="w-full">
                    <option value="JOURNAL_ENTRY_TYPE_STANDARD">Standard</option>
                    <option value="JOURNAL_ENTRY_TYPE_ADJUSTING">Adjusting</option>
                    <option value="JOURNAL_ENTRY_TYPE_CLOSING">Closing</option>
                    <option value="JOURNAL_ENTRY_TYPE_REVERSING">Reversing</option>
                    <option value="JOURNAL_ENTRY_TYPE_ACCRUAL">Accrual</option>
                  </Select>
                </div>
                <Input label="Entry Date" type="date" value={formData.entry_date} onChange={(e) => handleChange('entry_date', e.target.value)} required />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lines</CardTitle>
              <CardDescription>
                Total Debits: {totalDebits.toFixed(2)} | Total Credits: {totalCredits.toFixed(2)}
                {isBalanced ? (
                  <span className="ml-2 text-green-600">Balanced</span>
                ) : (
                  <span className="ml-2 text-red-600">Not balanced (diff: {Math.abs(totalDebits - totalCredits).toFixed(2)})</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {lines.map((line, index) => (
                <div key={index} className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Input label={index === 0 ? 'Account ID' : undefined} type="text" value={line.account_id} onChange={(e) => handleLineChange(index, 'account_id', e.target.value)} placeholder="Account ID" required />
                  </div>
                  <div className="w-28">
                    <Input label={index === 0 ? 'Debit' : undefined} type="text" value={line.debit} onChange={(e) => handleLineChange(index, 'debit', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="w-28">
                    <Input label={index === 0 ? 'Credit' : undefined} type="text" value={line.credit} onChange={(e) => handleLineChange(index, 'credit', e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="w-40">
                    <Input label={index === 0 ? 'Description' : undefined} type="text" value={line.description} onChange={(e) => handleLineChange(index, 'description', e.target.value)} placeholder="Line memo" />
                  </div>
                  {lines.length > 2 && <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(index)}>Remove</Button>}
                </div>
              ))}
              <Button type="button" variant="secondary" onClick={addLine}>Add Line</Button>
            </CardContent>
            <CardFooter className="flex justify-end gap-3">
              <Link to="/billing/journal-entries"><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
              <Button type="submit" disabled={isSubmitting || !isBalanced}>
                {isSubmitting ? t('common.creating') : 'Create Entry'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
});

export default JournalEntryCreatePage;
